<template>
    <BaseWidget
        :id="id"
        :widget-name="widgetName"
        :has-content="!!content"
        :is-loading="isLoading"
        :is-expanded="isExpanded"
        :configuration-data="configurationData"
        :widget-type="widgetType"
        :widget-last-updated="widgetLastUpdated"
        :needs-attention="needsAttention"
        v-on="$listeners"
    >
        <div v-show="!isLoading" class="widget-content">
            <div class="widget-inner">
                <div class="wrapper-float">
                    <div v-if="isExpanded" class="inline">{{ $t('reporting.commonLabels.callType') }}:</div>
                    <div class="widget-selectbox-wrapper" @mousedown.stop.prevent>
                        <SelectBox
                            :value="selectedOption"
                            style-class="inline"
                            :options="callTypesOptions"
                            :search="false"
                            @input="setSelectedOptions({ widgetId: id, selectedOption: $event })"
                        />
                    </div>
                </div>
                <div v-show="chartDataEmpty" class="no-widget">
                    <div class="no-widget-data">
                        <h2>{{ $t('common.noData') }}</h2>
                        <p>{{ $t('dashboard.otherConfiguration') }}</p>
                    </div>
                </div>
                <div v-show="!chartDataEmpty" class="exception-time-widget">
                    <div class="widget-chart-label">
                        <span>{{ $t('dashboard.widgetType6.numberOfCalls') }}</span>
                    </div>
                    <div ref="widgetChartRef" class="exception-widget-chart"></div>
                </div>
            </div>
        </div>
    </BaseWidget>
</template>

<script type="text/babel">
import { mapState, mapMutations, mapGetters } from 'vuex';
import Chartist from 'ascom-chartist-logscale';
import BaseWidget from '../BaseWidget';
import debounce from 'lodash-es/debounce';
import { getPreviousWeekArray } from '../widgetHelpers';
import SelectBox from '../../common/SelectBox.vue';
import widgetInterface from './common/widgetInterface';
import WidgetConfigurationTable from './common/WidgetConfigurationTable.vue';
import { mounted, beforeDestroy } from './common/chartsLifecycleHooks';
import { configurationData } from './common/widgetsComputedProps';
import { makeChart } from './common/chartingHelpers';
import { DEBOUNCE_CHARTS_REFRESH } from '../../../constants';

export default {
    components: {
        BaseWidget,
        SelectBox,
        WidgetConfigurationTable,
    },

    props: widgetInterface,

    data() {
        return {
            defaultCall: 1,
            instanceChart: null,
            debouncedMakeChart: debounce(() => this.makeChart(), DEBOUNCE_CHARTS_REFRESH),
            needsAttention: false,
        };
    },

    computed: {
        ...mapGetters({
            userType: 'user/userType',
            getSelectedOptionById: 'dashboard/getSelectedOptionById',
        }),

        ...mapState({
            navCollapsed: 'navCollapsed',
            callTypes: 'callTypes',
            callTypesOptions: state => state.callTypes.map(({ id, name }) => ({ value: id, name })),
            allAssignmentAreas: 'assignmentArea',
        }),

        /**
         * Returns widget selected option
         * @returns {number}
         */
        selectedOption() {
            return this.getSelectedOptionById(this.id) || this.defaultCall;
        },

        configurationData,

        /**
         * Returns widget name
         * @returns {string}
         */
        widgetName() {
            return this.configuration.name || this.$t('dashboard.widgetType6.name');
        },

        /**
         * Gets sums of delayed calls by day for current call type for last 7 days
         * @returns {Array}
         */
        chartDataDelayed() {
            return (this.content ? (this.content[this.selectedOption] || {}).delayed : []) || [];
        },

        /**
         * Gets sums of late calls by day for current call type for last 7 days
         * @returns {Array}
         */
        chartDataLate() {
            return (this.content ? (this.content[this.selectedOption] || {}).late : []) || [];
        },

        /**
         * Generates object for graph data with labels for tooltips
         * @returns {Object} data for graph
         */
        chartData() {
            let labels = getPreviousWeekArray(new Date());

            let delayedSeries = this.chartDataDelayed.map(time => ({
                meta: this.$t('dashboard.widgetType6.delayed'),
                value: time,
            }));
            let lateSeries = this.chartDataLate.map(time => ({
                meta: this.$t('dashboard.widgetType6.late'),
                value: time,
            }));

            return {
                labels,
                series: [delayedSeries, lateSeries],
            };
        },

        /**
         * Checks whether or not content data contains call statistic
         * @return {Boolean}
         */
        chartDataEmpty() {
            return [...this.chartDataDelayed, ...this.chartDataLate].reduce((c, v) => c + v, 0) <= 0;
        },
    },
    watch: {
        navCollapsed() {
            this.debouncedMakeChart();
        },
    },
    mounted,

    beforeDestroy,

    methods: {
        ...mapMutations({
            setSelectedOptions: 'dashboard/SET_SELECTED_WIDGET_DATA',
        }),

        makeChart,

        drawChart(ref, data) {
            let options = {
                seriesBarDistance: 12,
                height: this.isPrintver ? 300 : undefined,
                width: this.isPrintver ? 604 : undefined,
                axisX: {
                    showGrid: false,
                    offset: 44,
                },
                axisY: {
                    onlyInteger: true,
                    offset: 50,
                },
                plugins: [
                    Chartist.plugins.legend({
                        legendNames: [
                            this.$t('dashboard.widgetType6.delayed'),
                            this.$t('dashboard.widgetType6.late'),
                        ],
                        className: 'exception-widget-legend',
                        clickable: false,
                    }),
                    Chartist.plugins.tooltip(),
                ],
                chartPadding: {
                    top: 15,
                    right: 0,
                    bottom: 0,
                    left: 0,
                },
            };
            if (this.instanceChart) {
                this.instanceChart.update(data, options);
            } else {
                this.instanceChart = new Chartist.Bar(ref, data, options);
            }
            window.addEventListener('resize', this.debouncedMakeChart);
        },

        setNeedsAttention(value) {
            this.needsAttention = value;
        },
    },
};
</script>
